import { createFileRoute } from "@tanstack/react-router";
import { useMemo } from "react";
import { useActions, useMe, useSignalDefinitions, useSignals, useSummary } from "../api/hooks";
import { SummaryCard } from "../components/summary-card";
import { SignalCard } from "../components/signal-card";
import { Button, Chip, Empty, ErrorBox, Loading, Page, Table } from "../components/ui";
import { latestByScope, toneFor, unitOf } from "../lib/signals";
import { fmtNumber, title } from "../lib/format";

export const Route = createFileRoute("/report")({ component: Report });

const headline = ["after_hours_pct", "meeting_load_pct", "one_on_one_coverage_pct", "messages_per_day"];
const teamKeys = ["after_hours_pct", "meeting_load_pct", "one_on_one_coverage_pct", "items_closed_per_person_week", "team_cycle_time_days", "overdue_items_per_person"];

/** The weekly report: the company briefing, the headline signals, every team above the
 *  floor, open actions and measured outcomes — laid out to print (⌘P → save as PDF). */
function Report() {
  const me = useMe();
  const isAdmin = me.data?.roles.some((r) => r === "hr_admin" || r === "platform_admin") ?? false;
  const defs = useSignalDefinitions();
  const org = useSignals({ scope_type: "org", scope_key: "all" });
  const teams = useSignals({ scope_type: "team" });
  const summary = useSummary("org", "all");
  const openAll = useActions({ state: "recommended,awaiting_approval,assigned,accepted,in_progress" });
  // The report names no individual: person-audience (Management) actions stay on the Actions page.
  const open = { ...openAll, data: openAll.data?.filter((a) => a.audience_type !== "person") };
  const closed = useActions({ state: "completed" });
  const orgLatest = useMemo(() => (org.data ? latestByScope(org.data) : new Map()), [org.data]);
  const teamLatest = useMemo(() => (teams.data ? latestByScope(teams.data) : new Map()), [teams.data]);
  const teamNames = useMemo(() => [...new Set([...teamLatest.keys()].map((k) => k.split("|")[2]))].sort(), [teamLatest]);
  const measured = (closed.data ?? []).filter((a) => a.measured_outcome != null);
  const improved = measured.filter((a) => (a.measured_outcome as { improved?: boolean }).improved).length;
  const week = summary.data?.period_start ?? [...orgLatest.values()][0]?.latest.period_start;
  if (!isAdmin) return <Page title="Weekly report"><Empty title="Admins only" body="The weekly report covers the whole organisation; managers get the same for their line on Team health." /></Page>;
  return (
    <Page
      title="Weekly report"
      sub={week ? `Week of ${week}. Aggregates only; every number carries the population behind it.` : "Aggregates only; every number carries the population behind it."}
      actions={<Button variant="light" className="print:hidden" onClick={() => window.print()}>Print / save as PDF →</Button>}
    >
      <div className="mb-3"><SummaryCard summary={summary.data} pending={summary.isPending} title="The company, in plain words" /></div>
      {org.isError && <ErrorBox error={org.error} />}
      {org.isPending ? <Loading /> : (
        <div className="grid grid-cols-4 gap-3">
          {headline.map((k) => (
            <SignalCard key={k} label={title(k).replace(" Pct", " %")} unit={unitOf(defs.data, k)} entry={orgLatest.get(`${k}|org|all`)} />
          ))}
        </div>
      )}
      <div className="grid grid-cols-3 gap-3 mt-3">
        <div className="card kpi"><h4>Open team-level actions</h4><div className="v">{open.data?.length ?? "—"}</div><div className="delta">{openAll.data ? `${openAll.data.length - (open.data?.length ?? 0)} more about individuals, on the Actions page` : ""}</div></div>
        <div className="card kpi"><h4>Outcomes measured</h4><div className="v">{measured.length ? `${improved} of ${measured.length}` : "—"}</div><div className="delta">completed actions that moved their signal the right way</div></div>
        <div className="card kpi"><h4>Teams above the floor</h4><div className="v">{teamNames.length}</div></div>
      </div>
      <h3 className="text-[15px] font-bold mt-6 mb-2 break-before">Every team this week</h3>
      {teams.isPending ? <Loading /> : (
        <Table
          rows={teamNames}
          keyOf={(t) => t}
          empty="No team above the floor yet"
          cols={[
            { h: "Team", cell: (t) => <span className="font-semibold">{t}</span> },
            ...teamKeys.map((k) => ({
              h: title(k).replace(" Pct", " %"),
              w: "120px",
              cell: (t: string) => {
                const e = teamLatest.get(`${k}|team|${t}`);
                if (!e) return <span className="text-muted">—</span>;
                const tone = toneFor(k, e.latest.value);
                const v = `${fmtNumber(e.latest.value)}${unitOf(defs.data, k) === "pct" ? "%" : ""}`;
                return tone === "neutral" ? <span>{v}</span> : <Chip tone={tone}>{v}</Chip>;
              },
            })),
            { h: "n", w: "60px", cell: (t) => <span className="text-muted">{teamLatest.get(`after_hours_pct|team|${t}`)?.latest.population_n ?? "—"}</span> },
          ]}
        />
      )}
      <h3 className="text-[15px] font-bold mt-6 mb-2">Open team-level actions</h3>
      {open.isPending ? <Loading /> : (
        <Table
          rows={open.data ?? []}
          keyOf={(a) => a.id}
          empty="Nothing open"
          cols={[
            { h: "Action", cell: (a) => <><span className="font-semibold">{a.title}</span>{a.rationale && <span className="sub">{a.rationale}</span>}</> },
            { h: "Audience", w: "200px", cell: (a) => <span className="text-muted">{title(a.audience_type)} · {a.audience_key}</span> },
            { h: "State", w: "140px", cell: (a) => <Chip>{title(a.state)}</Chip> },
          ]}
        />
      )}
      <p className="text-[11.5px] text-muted mt-6">Generated by Humindly. Aggregates are shown only for groups at or above the organisation's privacy floor; no individual is named on this page.</p>
    </Page>
  );
}
